import type { ReactNode } from "react";
import { Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { Commitment, Lod } from "design-scope";

export const RESPONSIBILITIES = [
  "Architect",
  "Structural Engineer",
  "MEP Engineer",
  "General Contractor",
  "Owner"
];

/** An always-visible labeled field — no click-to-reveal. */
export function Field({
  label,
  hint,
  required,
  info,
  extra,
  action,
  children
}: {
  label: string;
  hint?: string;
  required?: boolean;
  info?: string;
  /** Sits right after the label, e.g. a status tag. */
  extra?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center gap-1">
        <Typography.Text style={{ fontSize: 12 }}>{label}</Typography.Text>
        {required && (
          <Typography.Text style={{ color: "var(--ciq-red-700)" }}>
            *
          </Typography.Text>
        )}
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ color: "var(--ciq-gray-500)" }} />
          </Tooltip>
        )}
        {hint && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        )}
        {extra}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </div>
  );
}

/** Gray caption over a group of fields, with an optional right-side action. */
export function Section({
  title,
  info,
  action,
  children
}: {
  title: string;
  info?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-1">
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {title}
        </Typography.Text>
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ color: "var(--ciq-gray-500)" }} />
          </Tooltip>
        )}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

export function blankCommitment(defaultLodId = "", lod: Lod = 1): Commitment {
  return {
    designPackageId: "",
    milestoneId: null,
    lod,
    levelOfDetailValueId: defaultLodId,
    notes: ""
  };
}

export function lodFromOptionIndex(index: number): Lod {
  return Math.min(5, Math.max(1, index + 1)) as Lod;
}
